import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress,
  Alert, List, ListItem, ListItemText, ListItemIcon, Chip, Divider, IconButton,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CircleIcon from '@mui/icons-material/Circle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import { listNotifications, markNotificationRead, markAllNotificationsRead } from '../services/api';

const iconMap = {
  investment: <MonetizationOnIcon sx={{ color: '#478289' }} />,
  document: <DescriptionIcon sx={{ color: '#1976d2' }} />,
  default: <InfoIcon sx={{ color: '#757575' }} />,
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await listNotifications();
      setNotifications(res.data?.notifications || res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load notifications');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (_) {}
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>Notifications</Typography>
              {unreadCount > 0 && (
                <Chip label={`${unreadCount} unread`} sx={{ mt: 1, color: 'white', borderColor: 'white' }} variant="outlined" />
              )}
            </Box>
            {unreadCount > 0 && (
              <Button variant="contained" startIcon={<DoneAllIcon />} onClick={handleMarkAllRead}
                sx={{ backgroundColor: 'white', color: '#052d50', '&:hover': { backgroundColor: '#e0e0e0' } }}>
                Mark All Read
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {notifications.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 8 }}>
            <NotificationsIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">No notifications yet</Typography>
          </Card>
        ) : (
          <Card>
            <List disablePadding>
              {notifications.map((n, i) => (
                <React.Fragment key={n.id || i}>
                  <ListItem
                    sx={{
                      backgroundColor: n.read ? 'transparent' : '#e3f2fd',
                      cursor: !n.read ? 'pointer' : 'default',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                    onClick={() => !n.read && handleMarkRead(n.id)}
                    secondaryAction={
                      !n.read && (
                        <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}>
                          <CircleIcon sx={{ fontSize: 12, color: '#478289' }} />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemIcon>{iconMap[n.type] || iconMap.default}</ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: n.read ? 400 : 600 }}>{n.title || n.message}</Typography>}
                      secondary={
                        <>
                          {n.body && <Typography variant="body2" color="textSecondary">{n.body}</Typography>}
                          <Typography variant="caption" color="textSecondary">
                            {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {i < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default Notifications;
